import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { ApiError } from '@/api/client';
import { acceptInvitation } from '@/api/rbac';
import { useAuth } from '@/features/auth/useAuth';

const schema = z
  .object({
    name: z.string().min(1, 'Your name is required').max(255),
    password: z.string().min(12, 'Use at least 12 characters'),
    password_confirmation: z.string(),
  })
  .refine((d) => d.password === d.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });

type FormValues = z.infer<typeof schema>;

/**
 * Redeem an invitation link.
 *
 * Reachable signed in OR signed out — the token is the authorisation either
 * way. An already-authenticated user simply joins the organisation; a new one
 * sets a name and password first.
 */
export function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const fail = (e: unknown) => {
    setError(
      e instanceof ApiError
        ? (e.fieldErrors.token?.[0] ?? e.detail ?? e.title)
        : 'Something went wrong. Please try again.',
    );
  };

  // Already signed in: joining needs no credentials at all.
  const joinDirectly = async () => {
    setError(null);
    setJoining(true);
    try {
      await acceptInvitation({ token: token ?? '' });
      // Full reload so the session, memberships and permissions are all
      // refetched for the organisation just joined.
      window.location.assign('/');
    } catch (e) {
      fail(e);
    } finally {
      setJoining(false);
    }
  };

  const onSubmit = async (values: FormValues) => {
    setError(null);
    try {
      await acceptInvitation({ token: token ?? '', ...values });
      window.location.assign('/');
    } catch (e) {
      fail(e);
    }
  };

  if (!token) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', p: 2 }}>
        <Alert severity="error" variant="outlined">
          This invitation link is incomplete. Ask whoever invited you to send a new one.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 440 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h1" sx={{ mb: 1 }}>
            Accept invitation
          </Typography>

          {error && (
            <Alert severity="error" variant="outlined" sx={{ my: 2 }}>
              {error}
            </Alert>
          )}

          {isLoading ? null : isAuthenticated ? (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                You are signed in. Accepting adds this organisation to your account.
              </Typography>
              <Button
                variant="contained"
                size="large"
                disabled={joining}
                onClick={() => void joinDirectly()}
              >
                {joining ? 'Joining…' : 'Join organisation'}
              </Button>
              <Button variant="text" onClick={() => void navigate('/')}>
                Not now
              </Button>
            </Stack>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your account to join. Your email is already confirmed by this link.
              </Typography>

              <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
                <Stack spacing={2}>
                  <TextField
                    label="Your name"
                    autoComplete="name"
                    autoFocus
                    fullWidth
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message}
                    {...register('name')}
                  />
                  <TextField
                    label="Password"
                    type="password"
                    autoComplete="new-password"
                    fullWidth
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message ?? 'At least 12 characters'}
                    {...register('password')}
                  />
                  <TextField
                    label="Confirm password"
                    type="password"
                    autoComplete="new-password"
                    fullWidth
                    error={Boolean(errors.password_confirmation)}
                    helperText={errors.password_confirmation?.message}
                    {...register('password_confirmation')}
                  />
                  <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
                    {isSubmitting ? 'Joining…' : 'Accept and create account'}
                  </Button>
                </Stack>
              </form>

              <Typography variant="body2" sx={{ mt: 3 }}>
                Already have an account?{' '}
                <Link component={RouterLink} to="/login">
                  Sign in first
                </Link>
                , then open this link again.
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
